/**
 * Baby welcome decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const babyWelcomeDecorationsPack: ToilePack = {
    slug: 'baby-welcome-decorations',
    category: 'occasions',
    name: 'Baby welcome decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'door', label: 'Door', subject: 'a front door with balloons and a sign reading "Welcome Baby"'},
        {key: 'room', label: 'Room', subject: 'a room with pastel balloons and teddies'},
        {key: 'cradle', label: 'Cradle', subject: 'a cradle with flowers'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'soft pastel decoration with balloon garlands, paper clouds and soft toys, gentle daylight from a window camera-left, a light airy balance with soft shadows', palette: 'baby pink, baby blue, mint and cream', framing: 'eye-level view, the whole setup in frame'},
        {key: 'neutral', label: 'Boho neutral', style: 'neutral boho decoration with dried flowers, rattan pieces and white and beige balloons, warm afternoon light from camera-right, a soft warm balance with gentle shadows', palette: 'beige, sand, white and sage', framing: 'three-quarter view with some of the room around it'},
        {key: 'floral', label: 'Fresh flowers', style: 'fresh flower decoration with jasmine, roses and marigold strings, bright morning light from overhead, a fresh clear balance with crisp shadows', palette: 'white, pink and yellow with green leaves', framing: 'straight-on view, symmetric, the decoration centered'},
    ],
    directions: ['twin babies', 'baby girl', 'baby boy'],
    directionHint: 'try: twin babies',
    seo: {
        title: 'Baby welcome decoration: door, room, cradle',
        metaDescription: 'Nine baby welcome decoration pictures for a homecoming: a door, a room and a cradle, in pastel, boho neutral and fresh flower looks.',
        h1: 'Baby welcome decoration: door, room and cradle',
        tagline: 'Nine pictures, three homecoming spots, each in a pastel, a boho neutral and a fresh flower look.',
        intro: "Baby welcome decoration for a newborn's homecoming: a front door with balloons and a sign reading Welcome Baby, a room with pastel balloons and teddies, and a cradle with flowers. Each spot is shown in a soft pastel look with balloon garlands and paper clouds, a boho neutral look with dried flowers and rattan, and a fresh flower look with jasmine, roses and marigold. Type a twist, such as twin babies or baby girl, and the same spots carry it.",
        useCases: [
            'a front door for a homecoming',
            'a room ready for a newborn',
            'a cradle with flowers',
            'a welcome sign for family visitors',
        ],
        faq: [
            {q: 'What does the door sign say?', a: 'It reads Welcome Baby. The other pictures have no lettering.'},
            {q: 'What are the three looks?', a: 'Soft pastel with baby pink, baby blue and mint; boho neutral with beige, sand and sage; and fresh flowers in white, pink and yellow.'},
            {q: 'Can I ask for a baby girl or twin babies?', a: 'Yes. Type a twist such as baby girl or twin babies, and the same spots carry it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs of a home, with no people in them.'},
        ],
    },
}
